/**
 * The smiler. Barely an enemy at all.
 *
 * It stands in a dark place at the far end of somewhere and is looked at. While
 * the player can see it, sanity bleeds. It backs off from the flashlight and
 * from anyone walking toward it, and if it is kept lit it simply leaves — so it
 * should almost never be the thing that kills a run. Its job is to make one
 * unlit corridor unbearable to walk down.
 */

import { clamp01 } from '../../core/math';
import { TUNING } from '../../core/config';
import {
  darkestNear,
  faceToward,
  holdStill,
  moveTo,
  retreatFrom,
  setMode,
  tryAttack,
  type Agent,
  type Behavior,
  type EntityWorld,
} from '../brain';
import { ENTITY_TUNING } from '../defs';

const T = ENTITY_TUNING.smiler;

/**
 * How brightly the player is lighting this thing up: the room's own light plus
 * the flashlight, but only when the beam is actually pointed at it.
 */
function litness(a: Agent, w: EntityWorld): number {
  let l = w.lightAt(a.state.pos);
  if (w.player.flashlightOn && a.state.visible && a.state.distToPlayer < TUNING.flashlight.range) {
    const falloff = 1 - a.state.distToPlayer / TUNING.flashlight.range;
    l += 0.8 * falloff * clamp01(w.player.flashlightCharge / 30);
  }
  return l;
}

export const smilerBehavior: Behavior = {
  kind: 'smiler',

  spawn(a, w) {
    a.turnRate = 1.8;
    a.timer = a.rng.range(T.breathInterval[0], T.breathInterval[1]);
    a.timer2 = a.rng.range(9, 22);
    // Settle into the darkest spot within a few metres; a lit smiler is a joke.
    const dark = darkestNear(w, a.state.pos, 5);
    if (dark) {
      a.state.pos.x = dark.x;
      a.state.pos.z = dark.z;
    }
    a.anchor.x = a.state.pos.x;
    a.anchor.z = a.state.pos.z;
    a.dest.x = a.state.pos.x;
    a.dest.z = a.state.pos.z;
  },

  think(a, w) {
    const s = a.state;
    const lit = litness(a, w);
    const tooClose = s.distToPlayer < T.personalSpace;

    if (s.mode === 'flee') {
      // Keep backing away until it is dark again and nobody is coming.
      a.timer2 -= w.dt;
      if (lit < T.lightFlinch * 0.5 && !tooClose) {
        a.timer2 = a.rng.range(9, 22);
        setMode(a, w, 'dormant');
        return;
      }
      retreatFrom(a, w, w.player.pos, a.def.chaseSpeed, true);
      if (a.timer2 <= 0) {
        // It has been chased with a torch long enough. It goes, for good.
        a.retire = true;
      }
      return;
    }

    // The age guard stops a smiler placed a hair too near a fixture from
    // flinching on its very first tick and never being seen at all.
    if ((lit > T.lightFlinch && s.age > 2.5) || tooClose) {
      a.timer2 = T.fleePatience;
      w.audio.play('smiler-laugh', { pos: s.pos, gain: 0.55 });
      setMode(a, w, 'flee');
      return;
    }

    // Contact is possible but takes real effort, and it hurts almost not at all.
    if (s.distToPlayer <= a.def.attackRange) tryAttack(a, w);

    if (s.visible && s.distToPlayer < T.stareRange) {
      setMode(a, w, 'stalk');
      holdStill(a);
      faceToward(a, w.player.pos);

      // The whole point of the thing. Steeply distance-weighted, so noticing one
      // at the far end of a hall costs almost nothing and walking toward it is
      // the mistake.
      const closeness = 1 - clamp01(s.distToPlayer / T.stareRange);
      w.dread(s, a.def.sanityDrain * (0.15 + 0.85 * closeness));
      s.awareness = clamp01(s.awareness + w.dt * 0.9);

      a.timer -= w.dt;
      if (a.timer <= 0) {
        a.timer = a.rng.range(T.breathInterval[0], T.breathInterval[1]);
        w.audio.play('smiler-breath', { pos: s.pos, gain: 0.7 });
      }
      return;
    }

    setMode(a, w, 'dormant');
    s.awareness = Math.max(0, s.awareness - w.dt * 0.5);

    if (s.visible) {
      // Seen, but too far away to matter. It still refuses to move while watched;
      // a smiler that shuffles about is a monster, and this is a picture.
      holdStill(a);
      faceToward(a, w.player.pos);
      return;
    }

    // Unwatched, it drifts a little deeper into the dark. Never toward you.
    a.timer2 -= w.dt;
    if (a.timer2 <= 0) {
      a.timer2 = a.rng.range(9, 22);
      const dark = darkestNear(w, a.anchor, 9);
      if (dark) {
        a.dest.x = dark.x;
        a.dest.z = dark.z;
      }
    }
    moveTo(a, w, a.dest, a.def.walkSpeed);
  },
};
